import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  DashboardIcon,
  PersonIcon,
  ClockIcon,
  FileTextIcon,
  IdCardIcon,
  AvatarIcon,
  CalendarIcon,
  ReaderIcon,
  TimerIcon,
  ExitIcon,
  CameraIcon,
  LayersIcon,
  SewingPinIcon,
  ClipboardIcon,
  RulerHorizontalIcon,
} from '@radix-ui/react-icons';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isFiscal = user?.role === 'fiscal';
  const isVisualizador = user?.role === 'visualizador';

  const getDashboardLink = () => {
    if (isAdmin) return '/admin/dashboard';
    if (isFiscal) return '/fiscal/dashboard';
    if (isVisualizador) return '/visualizador/dashboard';
    return '/user/dashboard';
  };

  // Prefixo também: /admin/escala/<id> acende o link "Escala".
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const getRoleLabel = () => {
    if (isAdmin) return 'Admin';
    if (isFiscal) return 'Fiscal';
    if (isVisualizador) return 'Visualizador';
    return 'Usuário';
  };

  const adminLinks = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { to: '/admin/users', label: 'Militares', icon: <PersonIcon /> },
    { to: '/admin/civilians', label: 'Civis', icon: <AvatarIcon /> },
    { to: '/admin/time-records', label: 'Registros', icon: <ClockIcon /> },
    { to: '/admin/lancamento-massa', label: 'Lançamento', icon: <LayersIcon /> },
    { to: '/admin/escala', label: 'Escala', icon: <ClipboardIcon /> },
    { to: '/admin/reports', label: 'Relatórios', icon: <FileTextIcon /> },
    { to: '/admin/positions', label: 'Funções', icon: <IdCardIcon /> },
    { to: '/admin/licenses', label: 'Licenças', icon: <CalendarIcon /> },
    { to: '/admin/audit', label: 'Auditoria', icon: <ReaderIcon /> },
    { to: '/admin/auto-close', label: 'Fechamento', icon: <TimerIcon /> },
    { to: '/admin/face', label: 'Facial', icon: <CameraIcon /> },
    { to: '/admin/school-location', label: 'Localização', icon: <SewingPinIcon /> },
    { to: '/admin/limites', label: 'Limites', icon: <RulerHorizontalIcon /> },
  ];

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-container">
        <Link to={getDashboardLink()} className="navbar-brand">
          <span className="navbar-brand-icon">
            <ClockIcon width={20} height={20} />
          </span>
          <span className="navbar-brand-text">Registros</span>
        </Link>

        <button
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div className={`navbar-menu ${menuOpen ? 'active' : ''}`}>
          <div className="navbar-links">
            {isAdmin && adminLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${isActive(link.to) ? 'nav-link-active' : ''}`}
                title={link.label}
              >
                <span className="nav-link-icon">{link.icon}</span>
                <span className="nav-link-label">{link.label}</span>
              </Link>
            ))}

            {isFiscal && (
              <Link
                to="/fiscal/dashboard"
                className={`nav-link ${isActive('/fiscal/dashboard') ? 'nav-link-active' : ''}`}
              >
                <span className="nav-link-icon"><ClockIcon /></span>
                <span className="nav-link-label">Registro de Ponto</span>
              </Link>
            )}

            {isVisualizador && (
              <Link
                to="/visualizador/dashboard"
                className={`nav-link ${isActive('/visualizador/dashboard') ? 'nav-link-active' : ''}`}
              >
                <span className="nav-link-icon"><FileTextIcon /></span>
                <span className="nav-link-label">Relatórios</span>
              </Link>
            )}

            {!isAdmin && !isFiscal && !isVisualizador && (
              <Link
                to="/user/dashboard"
                className={`nav-link ${isActive('/user/dashboard') ? 'nav-link-active' : ''}`}
              >
                <span className="nav-link-icon"><DashboardIcon /></span>
                <span className="nav-link-label">Meu Painel</span>
              </Link>
            )}

            {!isAdmin && (
              <Link
                to="/escala"
                className={`nav-link ${isActive('/escala') ? 'nav-link-active' : ''}`}
                title="Escala de itinerantes"
              >
                <span className="nav-link-icon"><ClipboardIcon /></span>
                <span className="nav-link-label">Escala</span>
              </Link>
            )}
          </div>

          <div className="navbar-user">
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-role-badge">{getRoleLabel()}</span>
            </div>
            <button onClick={handleLogout} className="btn-logout" title="Sair">
              <ExitIcon width={16} height={16} />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
